import FiltersButton from "../FiltersButton";
import { useTranslation } from "react-i18next";
import MoreFiltersItem from "./MoreFiltersItem";
import { CloseButton } from "reusableComponents";
import { useDispatch, useSelector } from "react-redux";
import { selectFilters } from "store/filters/selectors";
import { updateFilters } from "store/filters/filtersSlice";
import { useState, useRef, useEffect, useCallback } from "react";

import styles from "./styles.module.scss";

const MoreFiltersButton = ({ filterOptions }) => {
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters);
  const [t] = useTranslation("global");
  const [showOtherFiltersDropdown, setShowOtherFiltersDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(filters.options || {});
  const [error, setError] = useState(null);

  const dropdownRef = useRef(null);
  const overlayRef = useRef(null);

  // Set selected filters based on filters.options when component mounts
  useEffect(() => {
    setSelectedFilters(filters.options || {});
  }, [filters.options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showOtherFiltersDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        overlayRef.current &&
        overlayRef.current.contains(event.target)
      ) {
        setShowOtherFiltersDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOtherFiltersDropdown]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showOtherFiltersDropdown) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showOtherFiltersDropdown]);

  // Toggle dropdown
  const toggleShowOtherFiltersDropdown = () => {
    setShowOtherFiltersDropdown((prev) => !prev);
  };

  // Handle filter change logic
  const handleFilterChange = useCallback((category, selectedOption) => {
    setSelectedFilters((prev) => {
      const currentSelections = prev[category] || [];
      const isSelected = currentSelections.includes(selectedOption);

      return {
        ...prev,
        [category]: isSelected
          ? currentSelections.filter((option) => option !== selectedOption)
          : [...currentSelections, selectedOption],
      };
    });
  }, []);

  // Submit filters and close dropdown
  const handleSubmit = () => {
    dispatch(updateFilters({ options: selectedFilters }));
    setShowOtherFiltersDropdown(false);
  };

  const handleClearFilters = () => {
    setSelectedFilters({});
    dispatch(updateFilters({ options: {} }));
  };

  const handleClose = () => {
    setSelectedFilters(filters.options || {});
    setShowOtherFiltersDropdown(false);
  };

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className={styles.moreFiltersWrapper}>
      <FiltersButton
        label={t("search.moreFilters")}
        isActive={Object.keys(filters.options || {}).some(key => 
          filters.options[key] && filters.options[key].length > 0
        )}
        onClick={toggleShowOtherFiltersDropdown}
      />

      {showOtherFiltersDropdown && (
        <div className={styles.dropdownOverlay} ref={overlayRef}>
          <div className={styles.moreFiltersDropdownWrapper} ref={dropdownRef}>
            <div className={styles.header}>
              <h3 className={styles.headerTxt}>{t("search.options")}</h3>
              <CloseButton closeCallback={handleClose} />
            </div>

            <div className={styles.scrollContainer}>
              <div className={styles.body}>
                <div className={styles.bodyCol}>
                  {filterOptions?.openingHours && (
                    <MoreFiltersItem
                      header={t("search.openingHours")}
                      options={filterOptions.openingHours}
                      onChange={(option) =>
                        handleFilterChange("openingHours", option.key)
                      }
                      selectedOptions={selectedFilters?.openingHours || []}
                      keySelector={(option) => option.key}
                    />
                  )}

                  {filterOptions?.characteristics && (
                    <MoreFiltersItem
                      header={t("search.characteristics")}
                      options={filterOptions.characteristics}
                      onChange={(option) =>
                        handleFilterChange("characteristics", option.id)
                      }
                      selectedOptions={selectedFilters?.characteristics || []}
                      keySelector={(option) => option.id}
                    />
                  )}

                  {filterOptions?.parkings && (
                    <MoreFiltersItem
                      header={t("search.parking")}
                      options={filterOptions.parkings}
                      onChange={(option) =>
                        handleFilterChange("parkings", option.id)
                      }
                      selectedOptions={selectedFilters?.parkings || []}
                      keySelector={(option) => option.id}
                    />
                  )}

                  {filterOptions?.mealsAndBreaks && (
                    <MoreFiltersItem
                      header={t("search.mealsAndBreaks")}
                      options={filterOptions.mealsAndBreaks}
                      onChange={(option) =>
                        handleFilterChange("mealsAndBreaks", option.id)
                      }
                      selectedOptions={selectedFilters?.mealsAndBreaks || []}
                      keySelector={(option) => option.id}
                    />
                  )}
                </div>
                <div className={styles.bodyCol}>
                  {filterOptions?.placeTypes && (
                    <MoreFiltersItem
                      header={t("search.typeOfReception")}
                      options={filterOptions.placeTypes}
                      onChange={(option) =>
                        handleFilterChange("placeTypes", option.id)
                      }
                      selectedOptions={selectedFilters?.placeTypes || []}
                      keySelector={(option) => option.id}
                    />
                  )}

                  {filterOptions?.layouts && (
                    <MoreFiltersItem
                      header={t("search.layout")}
                      options={filterOptions.layouts}
                      onChange={(option) =>
                        handleFilterChange("layouts", option.key)
                      }
                      selectedOptions={selectedFilters?.layouts || []}
                      keySelector={(option) => option.key}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className={styles.footer}>
              <div className={styles.btns}>
                <button className={styles.clearBtn} onClick={handleClearFilters}>
                  {t("buttons.clear")}
                </button>
                <button className={styles.submitBtn} onClick={handleSubmit}>
                  {t("buttons.confirm")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MoreFiltersButton;